"use client";
import Image from "next/image";
import { useState } from 'react';
import { avenirNextRegular, avenirNextMedium } from '@/styles/fonts';
import clsx from 'clsx';
import browserTour3 from '@/app/images/tour/browsertour3.png';
import sales from '@/app/images/tour/sales.png';
import prospecting from '@/app/images/tour/prospecting.png';
import identify from '@/app/images/tour/identify.png';
import AccordionArrow from "@/app/components/accordionArrow";

const menuItems = [
    {
        title: "See What Advertisers Spend on Each Publisher",
        content: `Get the complete low-down on all advertisers for any publisher. Uncover who's buying, which network they're using, and how
        much they're spending. Identify advertisers relevant to your industry who are already
        spending a significant amount of money with similar vendors, but not with you.`,
        image: browserTour3,
        alt: "Browser Tour",
    },
    {
        title: "Find Competing Publishers",
        content: `Use the "Similar Publishers" panel to discover the big player in your industry. Uncover who their biggest clients are and
        how much they're spending. Leverage this information to streamline your sales process
        and close more deals.`,
        image: sales,
        alt: "Sales",
    },
    {
        title: "Prospect New Advertisers",
        content: `Use the "Recommended Advertisers" panel to identify similar advertisers. Make the easy sell by finding advertisers that
        already buy ad space from publishers in your space.`,
        image: prospecting,
        alt: "Prospecting",
    },
    {
        title: "Identify the Biggest Spenders",
        content: `See which advertisers already spend a significant amount of money with similar vendors, but not with you. By seeing their
        exact ad creatives, landing pages, and ad spend, you can develop a pitch that's guaranteed
        to seal the deal.`,
        image: identify,
        alt: "Identify",
    },
];

export default function TourSalesProspecting() {
    const [tab, setTab] = useState(0);
    const toggleTab = (index: number) => {
        setTab(tab === index ? -1 : index);
    };

    const liClassName = clsx(
        avenirNextRegular.className,
        "text-[16px]",
        "text-[#000000]",
        "w-full",
        "border-t",
        "border-[#d8d8d8]"
    );
    const menuItemClassName = clsx(
        avenirNextMedium.className,
        "text-[22px]",
        "text-[#343f50]",
        "cursor-pointer",
        "pt-[36px]",
        "pb-[8px]",
        "sm:py-[40px]",
        "pl-[15px]",
        "sm:pl-[30px]",
        "lg:pl-0",
    );
    const itemClassName = clsx(
        "text-[#5e6977]",
        "text-[18px]",
        "leading-[1.8]",
        "transition-all",
        "duration-500",
    );

    return (
        <section className={clsx(
            "w-full",
            "text-[18px]",
            "text-[#5e6977]",
            avenirNextRegular.className,
            "grid",
            "sm:pt-[80px]",
            "text-left",
            "xs:grid-cols-1",
            "lg:grid-cols-[minmax(0,1fr)_minmax(0,400px)_minmax(0,400px)_minmax(0,1fr)]",
            "lg:gap-x-[40px]",
            "lg:items-stretch",
            "gap-[20px]",
            "overflow-visible")}>
            <div className="lg:col-start-3 lg:col-end-4 lg:row-start-1">
                <h2 className={clsx(
                    avenirNextMedium.className,
                    "text-[20px] sm:text-[30px]",
                    "leading-[1.1]",
                    "px-[15px]",
                    "lg:px-0",
                    "text-[#444847]",
                    "pb-[17px] sm:pb-[40px]",
                )}>Sales Prospecting & Client Services</h2>
                <ul>
                    {menuItems.map((item, index) => (
                        <li key={index} className={liClassName}>
                            <div className={menuItemClassName} onClick={() => toggleTab(index)}>
                                <div className="grid grid-cols-1  gap-[20px]">
                                    <div className="sm:ordeer-2 lg:order-1 lg:colspan-2 flex items-center justify-between gap-[20px]">
                                        <div>{item.title}</div>
                                        <AccordionArrow direction={tab === index ? "down" : "right"} />
                                    </div>
                                    <div className="sm:order-1 lg:order-2 lg:display-none"></div>
                                </div>
                            </div>
                            <div className={clsx(itemClassName)} style={{
                                maxHeight: tab === index ? "1000px" : "0",
                                opacity: tab === index ? 1 : 0,
                                pointerEvents: tab === index ? "auto" : "none",
                            }}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-[20px] items-start w-full">
                                    <div className="px-[15px] lg:pb-[20px] lg:pl-0 leading-[1.1] sm:order-2">{item.content}</div>
                                    <div className="w-full mr-[-50px] sm:mr-0 lg:hidden sm:order-1">
                                        <Image
                                            src={item.image}
                                            alt={item.alt}
                                            className="img-responsive accordian-img touraccordian-img w-1/2 sm:w-full h-auto"
                                        />
                                    </div>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
            {/* Left column: active item image, lg+ only */}
            <div className="hidden 
                    lg:flex
                    lg:col-start-1
                    lg:col-end-2
                    lg:row-start-1
                    lg:items-end 
                    lg:justify-start
                    lg:self-end">
                {tab >= 0 && (
                    <Image
                        src={menuItems[tab].image}
                        alt={menuItems[tab].alt}
                        className="w-full h-auto lg:min-w-[550px] lg:self-start"
                    />
                )}
            </div>
        </section>
    );
}
